import { useEffect, useState, type ReactNode } from 'react';

import { Wordmark } from './Wordmark.js';
import { UserMenu } from './UserMenu.js';
import { Stamp } from '../ui/Stamp.js';
import { useAuth } from '../../lib/auth-context.js';
import { useRoute } from '../../lib/route.js';

interface PageShellProps {
  children: ReactNode;
}

interface NavItem {
  href: string;
  label: string;
  isActive: (path: string) => boolean;
}

const NAV_ITEMS: ReadonlyArray<NavItem> = [
  { href: '#/', label: 'Análise', isActive: (p) => p === '/' },
  { href: '#/posts', label: 'Sugerir posts', isActive: (p) => p === '/posts' },
  {
    href: '#/analises',
    label: 'Análises salvas',
    isActive: (p) => p === '/analises' || p.startsWith('/analises/'),
  },
  {
    href: '#/posts/saved',
    label: 'Drafts salvos',
    isActive: (p) => p === '/posts/saved' || p.startsWith('/posts/saved/'),
  },
  { href: '#/cadencia', label: 'Cadência', isActive: (p) => p === '/cadencia' },
];

function NavLink({
  href,
  active,
  children,
  onClick,
}: {
  href: string;
  active: boolean;
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      className={
        'font-sans text-sm transition-colors ' +
        (active
          ? 'text-[var(--ink-primary)] underline decoration-[var(--accent-saffron)] decoration-2 underline-offset-[6px]'
          : 'text-[var(--ink-tertiary)] hover:text-[var(--ink-secondary)]')
      }
    >
      {children}
    </a>
  );
}

/**
 * Shell padrão. Reading-first em mobile/tablet, com espaço para margin notes
 * laterais em wide viewport (≥1024px).
 *
 * Header com wordmark próprio + carimbo de versão.
 * Footer rico com 3 colunas (privacy, roadmap, build) seguindo princípios
 * de elevação E1, E5 e E6 (docs/01 §6).
 */
export function PageShell({ children }: PageShellProps) {
  const { user, logout } = useAuth();
  const { path } = useRoute();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Fecha o menu mobile sempre que a rota muda
  useEffect(() => {
    setMobileOpen(false);
  }, [path]);

  // Trava o scroll do body enquanto o menu mobile está aberto
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [mobileOpen]);

  return (
    <div className="min-h-screen bg-[var(--bg-paper)]">
      <header className="sticky top-0 z-30 border-b border-[var(--border-subtle)] bg-[var(--bg-paper)]/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-4 sm:px-6 sm:py-5">
          <div className="flex items-baseline gap-6">
            <a href="#/" className="inline-flex items-baseline">
              <Wordmark size="md" />
            </a>
            {user && (
              <nav className="hidden items-baseline gap-5 md:flex">
                {NAV_ITEMS.map((item) => (
                  <NavLink key={item.href} href={item.href} active={item.isActive(path)}>
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden sm:inline-block">
              <Stamp>v0.2.0 · MVP</Stamp>
            </span>
            {user && <UserMenu user={user} onLogout={logout} />}
            {user && (
              <button
                type="button"
                onClick={() => setMobileOpen((v) => !v)}
                aria-label={mobileOpen ? 'Fechar menu' : 'Abrir menu'}
                aria-expanded={mobileOpen}
                className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper-elevated)] md:hidden"
              >
                <span aria-hidden className="block text-lg leading-none text-[var(--ink-primary)]">
                  {mobileOpen ? '×' : '☰'}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Drawer mobile — só aparece em viewports < md (768px) */}
        {user && mobileOpen && (
          <div className="md:hidden">
            <nav
              className="border-t border-[var(--border-subtle)] bg-[var(--bg-paper)] px-4 py-2"
              aria-label="Navegação principal"
            >
              {NAV_ITEMS.map((item) => {
                const active = item.isActive(path);
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={
                      'block border-b border-[var(--border-subtle)] py-3 font-sans text-base ' +
                      (active
                        ? 'text-[var(--ink-primary)] font-medium'
                        : 'text-[var(--ink-secondary)]')
                    }
                    style={
                      active
                        ? { boxShadow: 'inset 3px 0 0 var(--accent-saffron)', paddingLeft: '0.75rem' }
                        : undefined
                    }
                  >
                    {item.label}
                  </a>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 pb-24 pt-8 sm:px-6 sm:pb-32 sm:pt-12">{children}</main>

      <footer className="border-t border-[var(--border-subtle)] bg-[var(--bg-paper-elevated)]">
        <div className="mx-auto max-w-6xl px-6 py-10">
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
            <div>
              <h3 className="font-serif text-sm font-medium text-[var(--ink-primary)]">
                Privacidade
              </h3>
              <p className="mt-2 font-sans text-xs leading-relaxed text-[var(--ink-tertiary)]">
                Seu PDF é processado em memória e descartado quando a sessão expira. Sua API key
                de IA fica só em sessão — nunca em banco nem em log. BYOK estrito.
              </p>
            </div>
            <div>
              <h3 className="font-serif text-sm font-medium text-[var(--ink-primary)]">
                O que vem
              </h3>
              <ul className="mt-2 space-y-1 font-sans text-xs text-[var(--ink-tertiary)]">
                <li>→ Login (e-mail+senha + OAuth)</li>
                <li>→ Histórico de análises por user</li>
                <li>→ Assistente de conteúdo, hooks, cadência</li>
              </ul>
            </div>
            <div>
              <h3 className="font-serif text-sm font-medium text-[var(--ink-primary)]">
                Build
              </h3>
              <dl className="mt-2 space-y-1 font-mono text-[11px] text-[var(--ink-tertiary)]">
                <div>
                  <span className="text-[var(--ink-muted)]">version</span>{' '}
                  <span className="tabular-nums">0.2.0-pre</span>
                </div>
                <div>
                  <span className="text-[var(--ink-muted)]">stack</span> Fastify · React 19 ·
                  Postgres
                </div>
                <div>
                  <span className="text-[var(--ink-muted)]">mode</span> local · dev
                </div>
              </dl>
            </div>
          </div>
          <div className="mt-8 border-t border-[var(--border-subtle)] pt-4 font-sans text-[11px] text-[var(--ink-muted)]">
            SmartApply é uma ferramenta independente. Não afiliada ao LinkedIn.
          </div>
        </div>
      </footer>
    </div>
  );
}
